import { useState, useEffect, useRef } from 'react'
import { X } from 'lucide-react'

/**
 * InstallPrompt — appears after the couple's 2nd visit.
 * • Chrome/Android: uses native beforeinstallprompt → one-tap install
 * • iOS Safari: shows manual "tap Share → Add to Home Screen" guide
 * • Already installed as PWA: never shown
 * • Dismissed: stored in localStorage, never shown again
 */
export default function InstallPrompt() {
  const [show, setShow]     = useState(false)
  const [isIOS, setIsIOS]   = useState(false)
  const promptRef           = useRef(null)   // stores beforeinstallprompt event

  useEffect(() => {
    // Already running as installed app — don't show
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true

    if (isStandalone) return

    // Already dismissed
    if (localStorage.getItem('okoiya_install_dismissed') === 'true') return

    // Track visits
    const visits = parseInt(localStorage.getItem('okoiya_visits') || '0', 10) + 1
    localStorage.setItem('okoiya_visits', String(visits))

    const ua  = navigator.userAgent
    const ios = /iPhone|iPad|iPod/.test(ua) && !window.MSStream

    setIsIOS(ios)

    if (visits < 2) return  // Show only from 2nd visit onwards

    if (ios) {
      // iOS Safari doesn't fire beforeinstallprompt — show hint after 4s
      const t = setTimeout(() => setShow(true), 4000)
      return () => clearTimeout(t)
    }

    // Android / Chrome — listen for native prompt
    const onBeforeInstall = (e) => {
      e.preventDefault()
      promptRef.current = e
      setShow(true)
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstall)
    window.addEventListener('appinstalled', dismiss)

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall)
      window.removeEventListener('appinstalled', dismiss)
    }
  }, [])

  function dismiss() {
    setShow(false)
    localStorage.setItem('okoiya_install_dismissed', 'true')
  }

  async function install() {
    if (!promptRef.current) { dismiss(); return }
    promptRef.current.prompt()
    const { outcome } = await promptRef.current.userChoice
    if (outcome === 'accepted') {
      localStorage.setItem('okoiya_install_dismissed', 'true')
    }
    setShow(false)
  }

  if (!show) return null

  return (
    <>
      <style>{`
        @keyframes okoiyaSlideUp {
          from { transform: translateY(100px); opacity: 0; }
          to   { transform: none; opacity: 1; }
        }
      `}</style>

      <div style={{
        position:     'fixed',
        bottom:       20,
        left:         '50%',
        transform:    'translateX(-50%)',
        width:        'calc(100% - 32px)',
        maxWidth:     460,
        background:   'white',
        borderRadius: '1.25rem',
        boxShadow:    '0 12px 40px rgba(0,0,0,0.18)',
        border:       '1.5px solid rgba(196,130,106,0.25)',
        padding:      '1rem 1.25rem',
        display:      'flex',
        alignItems:   'center',
        gap:          '0.875rem',
        zIndex:       9999,
        animation:    'okoiyaSlideUp 0.4s cubic-bezier(0.34,1.56,0.64,1)',
      }}>

        {/* App icon */}
        <div style={{
          width:          48,
          height:         48,
          borderRadius:   12,
          background:     '#C4826A',
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'center',
          flexShrink:     0,
        }}>
          <img
            src="/okoiya-logo.png"
            alt="Okoiya"
            style={{ width:36, height:36, objectFit:'contain', filter:'brightness(10)' }}
          />
        </div>

        {/* Copy */}
        <div style={{ flex:1, minWidth:0 }}>
          <p style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'0.9rem', color:'#1A1A1A', margin:'0 0 0.2rem', lineHeight:1.3 }}>
            Add Okoiya to your home screen
          </p>
          {isIOS ? (
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#6B6B6B', margin:0, lineHeight:1.5 }}>
              Tap{' '}
              <span style={{ display:'inline-flex',alignItems:'center',gap:'2px',background:'#F3F0EA',borderRadius:4,padding:'0 4px',fontSize:'0.7rem' }}>
                ⎋ Share
              </span>
              {' '}→{' '}
              <strong>Add to Home Screen</strong>
            </p>
          ) : (
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#6B6B6B', margin:0 }}>
              For the best experience — feels just like an app
            </p>
          )}
        </div>

        {/* Action button — only on Android */}
        {!isIOS && (
          <button
            onClick={install}
            style={{
              fontFamily:   'Nunito Sans, sans-serif',
              fontWeight:   700,
              fontSize:     '0.78rem',
              color:        'white',
              background:   '#C4826A',
              border:       'none',
              borderRadius: 100,
              padding:      '0.5rem 1.1rem',
              cursor:       'pointer',
              flexShrink:   0,
              whiteSpace:   'nowrap',
            }}>
            Add
          </button>
        )}

        {/* Dismiss */}
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          style={{ background:'none', border:'none', cursor:'pointer', color:'#BCBCBC', flexShrink:0, padding:4, lineHeight:0 }}>
          <X size={16}/>
        </button>
      </div>
    </>
  )
}
